import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { finalize } from 'rxjs/operators';
import { defer, forkJoin, from } from 'rxjs';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { api_routes, db_tables, routes } from '@app/consts';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ReviewsService } from './services/reviews.service';
import { Review } from './models/review.model';
import { Router } from '@angular/router';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';

@Component({
  selector: 'app-reviews',
  templateUrl: './reviews.component.html',
  styleUrls: ['./reviews.component.scss']
})
export class ReviewsComponent implements OnInit {
  @ViewChild('excelexport') excelexport: any;

  excelColumns: any[] = [
    { field: 'employeeName', title: this.translate.instant("Reviews-Employee") },
    { field: 'reviewPlanName', title: this.translate.instant("Reviews-ReviewPlan") },
    { field: 'reviewPlanDescription', title: this.translate.instant("Reviews-ReviewPlanDescription") },
    { field: 'reviewPlanStartDate', title: this.translate.instant("Reviews-StartDate") },
    { field: 'reviewPlanEndDate', title: this.translate.instant("Reviews-EndDate") },
    { field: 'reviewers', title: this.translate.instant("Reviews-Reviewers") },
    { field: 'status', title: this.translate.instant("Reviews-Status") }
  ];

  public columns: any[] = [
    { field: "employee", title: "Employee", type: "employee", tableId: "tfi_VpDn" },
    { field: "reviewPlanName", title: "Review Plan", tableId: "tfi_VpDn" },
    { field: "reviewPlanDescription", title: "Review Plan Description", tableId: "tfi_VpDn" },
    { field: "reviewPlanStartDate", title: "Start Date", type: "date", tableId: "tfi_VpDn" },
    { field: "reviewPlanEndDate", title: "End Date", type: "date", tableId: "tfi_VpDn" },
    { field: "reviewers", title: "Reviewers", type: "employees", tableId: "tfi_VpDn" },
    { field: "status", subField: "name", title: "Status", type: 'chip', chipValue: ['Complete', 'Archived', 'In Progress', 'Pending'], tableId: "tfi_VpDn" },
  ];

  filterCategories: any[] = [
    { field: "ReviewPlan.Name", title: "Review Plan Name", type: "String", dataType: 'String' },
    { field: "ReviewPlan.Description", title: "Description", type: "String", dataType: 'String' },
    { field: "Employee.FirstName", title: "First Name", type: "String", dataType: 'String' },
    { field: "Employee.LastName", title: "Last Name", type: "String", dataType: 'String' },
    { field: "Employee.Id", title: "Employee ID", type: "String", dataType: 'String' },
    { field: "Status.Name", title: "Status", type: "String", dataType: 'String' },
  ];

  sortableColumns: any[] = [
    { field: "employee", sortValue: ["Employee.FirstName", "Employee.LastName", "Employee.Id"] },
    { field: "reviewPlanreviewPlanStartDateName", sortValue: "reviewPlan.name" },
    { field: "reviewPlanDescription", sortValue: "reviewPlan.description" },
    { field: "status", sortValue: "status.name" },
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean;
  pageSize: number = 10;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  formId: string = 'frm_rSF47qcFdGmOnw'
  dialogRef: any;
  formValid: any;
  searchValue: string;
  searchFilterString: string;
  sortString: string;
  filterString: string;
  excelData: any[];
  fileName: string = 'Reviews.xlsx';
  gridId: string = 'hubbubhr-reviews-grid';
  getRequest: any;

  constructor(
    private dialog: MatDialog,
    public translate: TranslateService,
    private reviewsService: ReviewsService,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.getReviews();
  }

  getReviews() {
    let filter;
    if (this.searchFilterString) {
      filter = this.searchFilterString;
    }
    if (this.filterString) {
      if (filter) {
        filter = `${filter} AND ${this.filterString}`;
      }
      else {
        filter = `${this.filterString}`;
      }
    }

    this.isLoading = true;

    this.getRequest = this.reviewsService.getReviews(this.pageSize, this.skip, this.sortString, filter)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(
        (res) => {
          this.gridDataResult = {
            data: this.formatData(res.data),
            total: res.total,
          }
        }
      );
  }

  formatData(data: Review[]) {
    let newArray = data.map(obj => ({
      ...obj,
      "reviewPlanId": obj.reviewPlan.id,
      "reviewPlanName": obj.reviewPlan.name,
      "reviewPlanDescription": obj.reviewPlan.description,
      "reviewPlanStartDate": obj.reviewPlan.startDate,
      "reviewPlanEndDate": obj.reviewPlan.endDate,
      "employeeName": `${obj.employee.firstName} ${obj.employee.lastName}`
    }));

    return newArray;
  }

  public allData() {
    this.overlayService.show('Loading');

    from(this.reviewsService.getAllReviews(this.sortString, this.filterString))
      .subscribe({
        next: (res) => {
          this.excelData = this.formatData(res.data);
          this.excelData = this.excelData.map(obj => ({
            ...obj,
            "reviewPlanStartDate": moment(obj.reviewPlanStartDate).format('YYYY-MM-DD'),
            "reviewPlanEndDate": moment(obj.reviewPlanEndDate).format('YYYY-MM-DD'),
            "reviewers": obj.reviewers.map(reviewer => `${reviewer.firstName} ${reviewer.lastName}`).join(', '),
            "status": obj.status.name
          }));

          // if I dont have this timeout first time I download the report the data will be blank
          setTimeout(() => {
            this.excelexport.save();
          }, 1000);
        },
        error: (e) => {
          this.overlayService.hide();
        },
        complete: () => this.overlayService.hide()
      });
  }

  getReview(review?: Review) {
    if (review === undefined) {
      this.openFormDialog();
    }
    else {
      this.router.navigate([`${routes.PERFORMANCE}/${routes.REVIEWS}/Editor/${review.id}`]);
    }
  }

  openFormDialog(review?: Review) {
    let formData = {
      id: review ? review.id : null,
      asOf: moment().format(),
      changeReason: '',
      changeReasonComments: '',
    };

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formId: this.formId,
      formData: formData
    };

    this.dialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

    const sub = this.dialogRef.componentInstance.emitFormData.subscribe((event) => {
      this.save(event)
    });
  }

  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData: any) {
    this.overlayService.show();
    if (!formData.id) {
    }
    else {
      this.reviewsService.updateReview(formData.id, formData)
        .pipe(
          finalize(() => this.overlayService.hide())
        )
        .subscribe(
          (res) => {
            this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
            this.dialogRef.close(true);
            this.getReviews();
          }
        );
    }
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `(ReviewPlan.Name like "${variant}") OR (ReviewPlan.Description like "${variant}") OR (Employee.FirstName like "${variant}") OR (Employee.LastName like "${variant}")`;
        }
      });

      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getReviews();
  }

}
